import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import httpClient, { extractApiError } from "../api/httpClient";
import { dataGridSx, PageHeader, PagePanel, PageShell, pageTypography } from "../components/PageLayout";
import { useUI } from "../contexts/UIContext";
import { useBranding } from "../contexts/BrandingContext";

const defaultCompany = { id: null, nome: "", piano: "FREE" };
const defaultUser = {
  id: null,
  username: "",
  password: "",
  ruolo: "MANAGER",
  localeId: "",
  email: "",
  nomeCompleto: "",
  telefono: "",
  attivo: true,
};
const defaultBrandingForm = {
  logoUrl: "",
  layout: "SIDEBAR",
  theme: "LIGHT",
  colorPrimary: "#3498db",
  colorSecondary: "#6c757d",
  colorAccent: "#e67e22",
  lightTextColor: "#1f2937",
  lightForegroundColor: "#ffffff",
  lightFieldColor: "#f8fafc",
  lightTableCellTextColor: "#3498db",
  darkTextColor: "#e2e8f0",
  darkForegroundColor: "#111b2e",
  darkFieldColor: "#1f2937",
  darkTableCellTextColor: "#7dd3fc",
  fontFamily: "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
  appFontSize: "16px",
  tableFontFamily: "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
  tableFontSize: "0.875rem",
  tableHeaderFontSize: "0.82rem",
  headerTitle: "ShiftFlow SaaS",
  faviconUrl: "",
  sidebarEnabled: true,
  darkModeToggleEnabled: true,
  customCss: "",
  contentMaxWidth: "100%",
  contentPaddingX: "24px",
  contentPaddingY: "24px",
  pageSurfaceRadius: "14px",
  pageShellGradientStart: "#d6eaf8",
  pageShellGradientMiddle: "#f1f5f9",
  pageShellGradientEnd: "#ffffff",
};

const buildPageShellGradient = (start, middle, end) => `linear-gradient(180deg, ${start} 0%, ${middle} 26%, ${end} 100%)`;
const BRANDING_EXPORT_SCHEMA_VERSION = 1;
const BRANDING_IMPORT_EXPORT_KEYS = [
  "logoUrl",
  "layout",
  "theme",
  "colorPrimary",
  "colorSecondary",
  "colorAccent",
  "lightTextColor",
  "lightForegroundColor",
  "lightFieldColor",
  "lightTableCellTextColor",
  "darkTextColor",
  "darkForegroundColor",
  "darkFieldColor",
  "darkTableCellTextColor",
  "fontFamily",
  "appFontSize",
  "tableFontFamily",
  "tableFontSize",
  "tableHeaderFontSize",
  "headerTitle",
  "faviconUrl",
  "sidebarEnabled",
  "darkModeToggleEnabled",
  "customCss",
  "contentMaxWidth",
  "contentPaddingX",
  "contentPaddingY",
  "pageSurfaceRadius",
  "pageShellGradientStart",
  "pageShellGradientMiddle",
  "pageShellGradientEnd",
  "pageShellGradient",
];

const normalizeBrandingForImportExport = (source) => {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    throw new Error("Formato branding non valido: atteso oggetto JSON.");
  }

  const brandingData = source.branding && typeof source.branding === "object" ? source.branding : source;
  const normalized = {
    ...defaultBrandingForm,
  };

  BRANDING_IMPORT_EXPORT_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(brandingData, key) && brandingData[key] !== undefined) {
      normalized[key] = brandingData[key];
    }
  });

  normalized.sidebarEnabled = Boolean(normalized.sidebarEnabled);
  normalized.darkModeToggleEnabled = Boolean(normalized.darkModeToggleEnabled);
  normalized.pageShellGradient = buildPageShellGradient(
    normalized.pageShellGradientStart || defaultBrandingForm.pageShellGradientStart,
    normalized.pageShellGradientMiddle || defaultBrandingForm.pageShellGradientMiddle,
    normalized.pageShellGradientEnd || defaultBrandingForm.pageShellGradientEnd
  );

  return normalized;
};

const fontOptions = [
  "'Manrope', 'IBM Plex Sans', 'Segoe UI', sans-serif",
  "'Inter', 'Segoe UI', sans-serif",
  "'Roboto', 'Segoe UI', sans-serif",
  "'Open Sans', 'Segoe UI', sans-serif",
  "'Source Sans Pro', 'Segoe UI', sans-serif",
];

function AdminEntityModal({ open, title, fields, value, onClose, onSubmit }) {
  const [form, setForm] = useState(value);

  useEffect(() => {
    setForm(value);
  }, [value]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {fields.map((field) => (
            <Grid item xs={12} sm={field.sm || 12} key={field.name}>
              <TextField
                fullWidth
                select={!!field.options}
                type={field.type || "text"}
                label={field.label}
                required={!!field.required}
                disabled={!!field.disabled}
                helperText={field.helperText || ""}
                value={form[field.name] ?? ""}
                onChange={(event) => setForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
              >
                {field.options?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UserModal({ open, value, locali, onClose, onSubmit }) {
  const [form, setForm] = useState(defaultUser);

  useEffect(() => {
    setForm({ ...defaultUser, ...(value || {}) });
  }, [value]);

  const isEdit = Boolean(form.id);
  const isManager = form.ruolo === "MANAGER";

  const updateField = (field) => (event) => {
    const fieldValue = field === "attivo" ? event.target.value === "true" : event.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: fieldValue };
      if (field === "ruolo" && fieldValue !== "MANAGER") {
        next.localeId = "";
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (!form.username?.trim()) {
      return;
    }
    if (!isEdit && !form.password?.trim()) {
      return;
    }
    if (form.password && form.password.length > 0 && form.password.length < 8) {
      return;
    }
    if (isManager && !form.localeId) {
      return;
    }

    onSubmit({
      ...form,
      username: form.username.trim(),
      password: form.password?.trim() || "",
      email: form.email?.trim() || "",
      nomeCompleto: form.nomeCompleto?.trim() || "",
      telefono: form.telefono?.trim() || "",
      localeId: form.localeId || "",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? "Modifica utente" : "Nuovo utente"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Username" value={form.username} onChange={updateField("username")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="password"
              required={!isEdit}
              label={isEdit ? "Password (opzionale)" : "Password"}
              value={form.password}
              onChange={updateField("password")}
              helperText={isEdit ? "Lascia vuoto per non modificarla" : "Almeno 8 caratteri"}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth required label="Ruolo" value={form.ruolo} onChange={updateField("ruolo")}>
              {[
                "SUPERADMIN",
                "ADMIN",
                "MANAGER",
                "DIPENDENTE",
              ].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Locale"
              value={form.localeId}
              onChange={updateField("localeId")}
              required={isManager}
              disabled={!isManager}
              helperText={isManager ? "Obbligatorio per MANAGER" : "Solo per MANAGER"}
            >
              <MenuItem value="">Nessuno</MenuItem>
              {(locali || []).map((locale) => (
                <MenuItem key={locale.id} value={String(locale.id)}>
                  {locale.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Stato" value={String(form.attivo)} onChange={updateField("attivo")}>
              <MenuItem value="true">Attivo</MenuItem>
              <MenuItem value="false">Disabilitato</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Email" value={form.email} onChange={updateField("email")} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Nome completo" value={form.nomeCompleto} onChange={updateField("nomeCompleto")} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Telefono" value={form.telefono} onChange={updateField("telefono")} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Admin() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [locali, setLocali] = useState([]);
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [companyModal, setCompanyModal] = useState({ open: false, value: defaultCompany });
  const [userModal, setUserModal] = useState({ open: false, value: defaultUser });
  const [passwordModal, setPasswordModal] = useState({ open: false, userId: null, username: "", nuovaPassword: "" });
  const [brandingForm, setBrandingForm] = useState(defaultBrandingForm);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const brandingImportInputRef = useRef(null);

  const { showNotification } = useUI();
  const { branding, upsertBranding, resetBranding, loadBranding } = useBranding();

  const fetchUsers = async () => {
    try {
      return await httpClient.get("/utenti");
    } catch (error) {
      return httpClient.get("/admin/utenti");
    }
  };

  const normalizeUser = (user, companyMap, localeMap) => ({
    id: user?.id,
    username: user?.username || "",
    ruolo: user?.ruolo || "DIPENDENTE",
    localeId: user?.localeId || user?.locale?.id || null,
    localeNome: user?.locale?.nome || localeMap.get(user?.localeId || user?.locale?.id) || "-",
    email: user?.email || "",
    nomeCompleto: user?.nomeCompleto || "",
    telefono: user?.telefono || "",
    attivo: typeof user?.attivo === "boolean" ? user.attivo : true,
    azienda: user?.azienda || null,
    aziendaNome:
      user?.azienda?.nome ||
      user?.companyName ||
      companyMap.get(user?.aziendaId || user?.companyId) ||
      "-",
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [companiesResponse, usersResponse, localiResponse] = await Promise.all([
        httpClient.get("/admin/aziende"),
        fetchUsers(),
        httpClient.get("/locali").catch(() => ({ data: [] })),
      ]);
      const nextCompanies = companiesResponse?.data || [];
      const nextLocali = localiResponse?.data || [];
      const companyMap = new Map(nextCompanies.map((company) => [company.id, company.nome]));
      const localeMap = new Map(nextLocali.map((locale) => [locale.id, locale.nome]));
      setCompanies(nextCompanies);
      setLocali(nextLocali);
      setUsers((usersResponse?.data || []).map((user) => normalizeUser(user, companyMap, localeMap)));
    } catch (error) {
      showNotification(extractApiError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setBrandingForm({
      ...defaultBrandingForm,
      ...branding,
      pageShellGradientStart: branding.pageShellGradientStart || defaultBrandingForm.pageShellGradientStart,
      pageShellGradientMiddle: branding.pageShellGradientMiddle || defaultBrandingForm.pageShellGradientMiddle,
      pageShellGradientEnd: branding.pageShellGradientEnd || defaultBrandingForm.pageShellGradientEnd,
      pageShellGradient:
        branding.pageShellGradient ||
        buildPageShellGradient(
          branding.pageShellGradientStart || defaultBrandingForm.pageShellGradientStart,
          branding.pageShellGradientMiddle || defaultBrandingForm.pageShellGradientMiddle,
          branding.pageShellGradientEnd || defaultBrandingForm.pageShellGradientEnd,
        ),
    });
  }, [branding]);

  const saveCompany = async (payload) => {
    try {
      if (!payload.id) {
        throw new Error("Creazione azienda non supportata da questa API");
      }

      await httpClient.put(`/admin/aziende/${payload.id}`, {
        nome: payload.nome,
        piano: payload.piano,
      });
      showNotification("Piano azienda aggiornato", "success");
      setCompanyModal({ open: false, value: defaultCompany });
      await loadData();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const saveUser = async (payload) => {
    try {
      const userPayload = {
        username: payload.username,
        ruolo: payload.ruolo,
        localeId: payload.ruolo === "MANAGER" && payload.localeId ? Number(payload.localeId) : null,
        email: payload.email || null,
        nomeCompleto: payload.nomeCompleto || null,
        telefono: payload.telefono || null,
        attivo: payload.attivo,
      };

      if (payload.password) {
        userPayload.password = payload.password;
      }

      if (payload.id) {
        await httpClient.put(`/utenti/${payload.id}`, userPayload);
        showNotification("Utente aggiornato", "success");
      } else {
        if (!payload.password) {
          throw new Error("Password obbligatoria in creazione");
        }
        await httpClient.post("/utenti", userPayload);
        showNotification("Utente creato", "success");
      }

      setUserModal({ open: false, value: defaultUser });
      await loadData();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const deleteCompany = async (id) => {
    try {
      await httpClient.delete(`/admin/aziende/${id}`);
      showNotification("Azienda eliminata", "success");
      await loadData();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const deleteUser = async (id) => {
    try {
      await httpClient.delete(`/utenti/${id}`);
      showNotification("Utente eliminato", "success");
      await loadData();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      if (user.attivo) {
        await httpClient.put(`/utenti/${user.id}/disabilita`);
        showNotification("Utente disabilitato", "success");
      } else {
        await httpClient.put(`/utenti/${user.id}/abilita`);
        showNotification("Utente riabilitato", "success");
      }
      await loadData();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const submitPasswordReset = async () => {
    try {
      if (!passwordModal.userId || !passwordModal.nuovaPassword) {
        return;
      }
      await httpClient.put(`/utenti/${passwordModal.userId}/cambio-password`, null, {
        params: { nuovaPassword: passwordModal.nuovaPassword },
      });
      showNotification("Password aggiornata", "success");
      setPasswordModal({ open: false, userId: null, username: "", nuovaPassword: "" });
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const filteredUsers = useMemo(
    () => (roleFilter === "ALL" ? users : users.filter((user) => user.ruolo === roleFilter)),
    [users, roleFilter]
  );

  const saveBranding = async () => {
    setBrandingLoading(true);
    try {
      await upsertBranding(brandingForm);
      showNotification("Branding aggiornato", "success");
    } catch (error) {
      showNotification(extractApiError(error), "error");
    } finally {
      setBrandingLoading(false);
    }
  };

  const handleBrandingField = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setBrandingForm((prev) => {
      const next = { ...prev, [field]: value };
      if (["pageShellGradientStart", "pageShellGradientMiddle", "pageShellGradientEnd"].includes(field)) {
        next.pageShellGradient = buildPageShellGradient(
          next.pageShellGradientStart || defaultBrandingForm.pageShellGradientStart,
          next.pageShellGradientMiddle || defaultBrandingForm.pageShellGradientMiddle,
          next.pageShellGradientEnd || defaultBrandingForm.pageShellGradientEnd,
        );
      }
      return next;
    });
  };

  const handleResetBranding = async () => {
    setBrandingLoading(true);
    try {
      await resetBranding();
      await loadBranding();
      showNotification("Branding ripristinato", "success");
    } catch (error) {
      showNotification(extractApiError(error), "error");
    } finally {
      setBrandingLoading(false);
    }
  };

  const exportBrandingJson = () => {
    try {
      const payload = normalizeBrandingForImportExport(brandingForm);
      const exportData = {
        schemaVersion: BRANDING_EXPORT_SCHEMA_VERSION,
        exportedAt: new Date().toISOString(),
        branding: payload,
      };
      const blob = new Blob([`${JSON.stringify(exportData, null, 2)}\n`], { type: "application/json;charset=utf-8" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `branding-layout-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      showNotification("Export branding completato", "success");
    } catch (error) {
      showNotification(error?.message || "Impossibile esportare il branding", "error");
    }
  };

  const openBrandingImportDialog = () => {
    if (!brandingImportInputRef.current) {
      return;
    }
    brandingImportInputRef.current.value = "";
    brandingImportInputRef.current.click();
  };

  const importBrandingJson = async (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) {
      return;
    }

    try {
      const fileContent = await selectedFile.text();
      const parsedJson = JSON.parse(fileContent);
      const importedBranding = normalizeBrandingForImportExport(parsedJson);
      setBrandingForm(importedBranding);
      showNotification("Branding importato nel form. Premi \"Salva branding\" per applicarlo.", "success");
    } catch (error) {
      showNotification(error?.message || "File branding non valido", "error");
    }
  };

  const companyColumns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "nome", headerName: "Azienda", minWidth: 180, flex: 1 },
      { field: "piano", headerName: "Piano", minWidth: 120, flex: 0.6 },
      {
        field: "actions",
        type: "actions",
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifica"
            onClick={() => setCompanyModal({ open: true, value: params.row })}
          />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Elimina" onClick={() => deleteCompany(params.id)} />,
        ],
      },
    ],
    []
  );

  const userColumns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "username", headerName: "Username", minWidth: 180, flex: 1 },
      { field: "nomeCompleto", headerName: "Nome", minWidth: 180, flex: 1 },
      { field: "email", headerName: "Email", minWidth: 220, flex: 1 },
      { field: "ruolo", headerName: "Ruolo", minWidth: 130, flex: 0.6 },
      { field: "localeNome", headerName: "Locale", minWidth: 150, flex: 0.7 },
      {
        field: "aziendaNome",
        headerName: "Azienda",
        minWidth: 180,
        flex: 1,
        valueGetter: (params) => params?.row?.aziendaNome || "-",
      },
      {
        field: "attivo",
        headerName: "Stato",
        minWidth: 120,
        flex: 0.5,
        renderCell: (params) => (
          <Chip
            label={params.value ? "Attivo" : "Disabilitato"}
            color={params.value ? "success" : "default"}
            size="small"
            variant={params.value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "actions",
        type: "actions",
        width: 200,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifica"
            onClick={() => {
              setUserModal({
                open: true,
                value: {
                  ...defaultUser,
                  ...params.row,
                  localeId: params.row?.localeId ? String(params.row.localeId) : "",
                  password: "",
                },
              });
            }}
          />,
          <GridActionsCellItem
            icon={params.row?.attivo ? <ToggleOffIcon /> : <ToggleOnIcon />}
            label={params.row?.attivo ? "Disabilita" : "Abilita"}
            onClick={() => toggleUserStatus(params.row)}
          />,
          <GridActionsCellItem
            icon={<LockResetIcon />}
            label="Cambia password"
            onClick={() =>
              setPasswordModal({
                open: true,
                userId: params.row?.id,
                username: params.row?.username || "",
                nuovaPassword: "",
              })
            }
          />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Elimina" onClick={() => deleteUser(params.id)} />,
        ],
      },
    ],
    [locali]
  );

  return (
    <PageShell>
      <PageHeader
        title="Console amministrazione"
        subtitle="Supervisione aziende e utenti con la stessa struttura a pannelli ordinati della vista turni."
      />

      <PagePanel tone="neutral" sx={{ p: 0 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="Aziende" />
          <Tab label="Utenti" />
          <Tab label="Branding" />
        </Tabs>
      </PagePanel>

      {tab === 0 && (
        <PagePanel tone="cool" sx={{ p: 1.5 }}>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Typography sx={pageTypography.panelCaption}>Gestione aziende registrate e piano abbonamento.</Typography>
          </Stack>
          {loading ? (
            <Skeleton variant="rounded" height={400} />
          ) : (
            <DataGrid rows={companies} columns={companyColumns} autoHeight disableRowSelectionOnClick pageSizeOptions={[10, 25]} sx={dataGridSx} />
          )}
        </PagePanel>
      )}

      {tab === 1 && (
        <PagePanel tone="warm" sx={{ p: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                select
                size="small"
                label="Filtro ruolo"
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value)}
                sx={{ minWidth: 180 }}
              >
                {["ALL", "SUPERADMIN", "ADMIN", "MANAGER", "DIPENDENTE"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "ALL" ? "Tutti" : option}
                  </MenuItem>
                ))}
              </TextField>
              <Typography sx={{ ...pageTypography.panelCaption, alignSelf: "center" }}>
                Gestione utenti completa: CRUD, stato attivo e reset password.
              </Typography>
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setUserModal({ open: true, value: defaultUser })}
            >
              Nuovo utente
            </Button>
          </Stack>
          {loading ? (
            <Skeleton variant="rounded" height={400} />
          ) : (
            <DataGrid rows={filteredUsers} columns={userColumns} autoHeight disableRowSelectionOnClick pageSizeOptions={[10, 25]} sx={dataGridSx} />
          )}
        </PagePanel>
      )}

      {tab === 2 && (
        <PagePanel tone="cool" sx={{ p: 1.5 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={pageTypography.panelTitle}>Branding aziendale</Typography>
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleResetBranding}
                disabled={brandingLoading}
              >
                Reset default
              </Button>
            </Stack>

            <PagePanel tone="neutral" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography sx={pageTypography.panelTitle}>Identita e struttura</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Header title" value={brandingForm.headerTitle} onChange={handleBrandingField("headerTitle")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Logo URL" value={brandingForm.logoUrl} onChange={handleBrandingField("logoUrl")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Favicon URL" value={brandingForm.faviconUrl} onChange={handleBrandingField("faviconUrl")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField select fullWidth label="Layout" value={brandingForm.layout} onChange={handleBrandingField("layout")}>
                      {["SIDEBAR", "TOP_NAV", "MINIMAL", "TWO_COLUMN"].map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField select fullWidth label="Theme" value={brandingForm.theme} onChange={handleBrandingField("theme")}>
                      {["LIGHT", "DARK", "CUSTOM"].map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Sidebar"
                      value={String(brandingForm.sidebarEnabled)}
                      onChange={(event) => setBrandingForm((prev) => ({ ...prev, sidebarEnabled: event.target.value === "true" }))}
                    >
                      <MenuItem value="true">Abilitata</MenuItem>
                      <MenuItem value="false">Disabilitata</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Toggle tema"
                      value={String(brandingForm.darkModeToggleEnabled)}
                      onChange={(event) => setBrandingForm((prev) => ({ ...prev, darkModeToggleEnabled: event.target.value === "true" }))}
                    >
                      <MenuItem value="true">Abilitato</MenuItem>
                      <MenuItem value="false">Disabilitato</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Stack>
            </PagePanel>

            <PagePanel tone="warm" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography sx={pageTypography.panelTitle}>Palette e superfici</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Primary" value={brandingForm.colorPrimary} onChange={handleBrandingField("colorPrimary")} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Secondary" value={brandingForm.colorSecondary} onChange={handleBrandingField("colorSecondary")} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Accent"
                      value={brandingForm.colorAccent}
                      onChange={handleBrandingField("colorAccent")}
                      helperText="Questo colore controlla anche i nomi Reperibili nella tabella turni."
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="color"
                      label="Gradient start"
                      value={brandingForm.pageShellGradientStart}
                      onChange={handleBrandingField("pageShellGradientStart")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="color"
                      label="Gradient middle"
                      value={brandingForm.pageShellGradientMiddle}
                      onChange={handleBrandingField("pageShellGradientMiddle")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="color"
                      label="Gradient end"
                      value={brandingForm.pageShellGradientEnd}
                      onChange={handleBrandingField("pageShellGradientEnd")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Raggio superfici" value={brandingForm.pageSurfaceRadius} onChange={handleBrandingField("pageSurfaceRadius")} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Larghezza contenuto" value={brandingForm.contentMaxWidth} onChange={handleBrandingField("contentMaxWidth")} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Padding contenuto X" value={brandingForm.contentPaddingX} onChange={handleBrandingField("contentPaddingX")} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Padding contenuto Y" value={brandingForm.contentPaddingY} onChange={handleBrandingField("contentPaddingY")} />
                  </Grid>
                </Grid>
              </Stack>
            </PagePanel>

            <PagePanel tone="cool" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography sx={pageTypography.panelTitle}>Tipografia app e tabella</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Font family" value={brandingForm.fontFamily} onChange={handleBrandingField("fontFamily")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Font size app" value={brandingForm.appFontSize} onChange={handleBrandingField("appFontSize")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Font righe tabella"
                      value={brandingForm.tableFontFamily}
                      onChange={handleBrandingField("tableFontFamily")}
                    >
                      {fontOptions.map((option) => (
                        <MenuItem key={option} value={option} sx={{ fontFamily: option }}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth label="Font size tabella" value={brandingForm.tableFontSize} onChange={handleBrandingField("tableFontSize")} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth label="Font size header tabella" value={brandingForm.tableHeaderFontSize} onChange={handleBrandingField("tableHeaderFontSize")} />
                  </Grid>
                </Grid>
              </Stack>
            </PagePanel>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <PagePanel tone="neutral" sx={{ p: 2, height: "100%" }}>
                  <Stack spacing={1.5}>
                    <Typography sx={pageTypography.panelTitle}>Tema Light</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Light font color"
                          value={brandingForm.lightTextColor}
                          onChange={handleBrandingField("lightTextColor")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Light foreground"
                          value={brandingForm.lightForegroundColor}
                          onChange={handleBrandingField("lightForegroundColor")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Light field color"
                          value={brandingForm.lightFieldColor}
                          onChange={handleBrandingField("lightFieldColor")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Light cell text"
                          value={brandingForm.lightTableCellTextColor}
                          onChange={handleBrandingField("lightTableCellTextColor")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </PagePanel>
              </Grid>
              <Grid item xs={12} md={6}>
                <PagePanel tone="neutral" sx={{ p: 2, height: "100%" }}>
                  <Stack spacing={1.5}>
                    <Typography sx={pageTypography.panelTitle}>Tema Dark</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Dark font color"
                          value={brandingForm.darkTextColor}
                          onChange={handleBrandingField("darkTextColor")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Dark foreground"
                          value={brandingForm.darkForegroundColor}
                          onChange={handleBrandingField("darkForegroundColor")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Dark field color"
                          value={brandingForm.darkFieldColor}
                          onChange={handleBrandingField("darkFieldColor")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Dark cell text"
                          value={brandingForm.darkTableCellTextColor}
                          onChange={handleBrandingField("darkTableCellTextColor")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </PagePanel>
              </Grid>
            </Grid>

            <PagePanel tone="neutral" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography sx={pageTypography.panelTitle}>Avanzate</Typography>
                <TextField fullWidth label="Custom CSS" value={brandingForm.customCss} onChange={handleBrandingField("customCss")} />
              </Stack>
            </PagePanel>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <input
                ref={brandingImportInputRef}
                type="file"
                accept="application/json,.json"
                onChange={importBrandingJson}
                style={{ display: "none" }}
              />
              <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={openBrandingImportDialog} disabled={brandingLoading}>
                Importa JSON
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportBrandingJson} disabled={brandingLoading}>
                Esporta JSON
              </Button>
              <Button variant="contained" onClick={saveBranding} disabled={brandingLoading}>
                Salva branding
              </Button>
              <Button variant="outlined" onClick={() => setBrandingForm({ ...defaultBrandingForm, ...branding })} disabled={brandingLoading}>
                Ripristina form
              </Button>
            </Stack>

            <PagePanel tone="neutral" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={pageTypography.panelTitle}>Anteprima live</Typography>
                  <Typography
                    sx={{
                      ...pageTypography.panelCaption,
                      px: 1,
                      py: 0.5,
                      borderRadius: 999,
                      backgroundColor: "rgba(52, 152, 219, 0.12)",
                      color: "#2980b9",
                    }}
                  >
                    aggiornata in tempo reale
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  {[
                    {
                      key: "LIGHT",
                      title: "Anteprima Light",
                      textColor: brandingForm.lightTextColor,
                      foregroundColor: brandingForm.lightForegroundColor,
                      fieldColor: brandingForm.lightFieldColor,
                      tableCellTextColor: brandingForm.lightTableCellTextColor,
                      surfaceBackground: brandingForm.pageShellGradient,
                    },
                    {
                      key: "DARK",
                      title: "Anteprima Dark",
                      textColor: brandingForm.darkTextColor,
                      foregroundColor: brandingForm.darkForegroundColor,
                      fieldColor: brandingForm.darkFieldColor,
                      tableCellTextColor: brandingForm.darkTableCellTextColor,
                      surfaceBackground: "linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)",
                    },
                  ].map((mode) => (
                    <Grid item xs={12} md={6} key={mode.key}>
                      <Paper
                        elevation={0}
                        sx={{
                          overflow: "hidden",
                          borderRadius: 3,
                          border: "1px solid rgba(189, 195, 199, 0.9)",
                          background: mode.surfaceBackground,
                          color: mode.textColor,
                          fontFamily: brandingForm.fontFamily,
                          p: 2,
                          minHeight: 300,
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Typography sx={{ ...pageTypography.panelTitle, color: mode.textColor }}>{mode.title}</Typography>
                          <div
                            style={{
                              borderRadius: 12,
                              padding: "12px",
                              backgroundColor: mode.foregroundColor,
                              border: `1px solid ${brandingForm.colorSecondary}66`,
                              color: mode.textColor,
                            }}
                          >
                            <div style={{ fontSize: 13, opacity: 0.75 }}>Header</div>
                            <div style={{ fontSize: 18, fontWeight: 800 }}>{brandingForm.headerTitle || "ShiftFlow SaaS"}</div>
                          </div>
                          <div
                            style={{
                              borderRadius: 12,
                              padding: "12px",
                              backgroundColor: mode.fieldColor,
                              border: `1px solid ${brandingForm.colorPrimary}66`,
                            }}
                          >
                            <div style={{ fontSize: 13, opacity: 0.75 }}>Field</div>
                            <div style={{ marginTop: 6, fontSize: 14 }}>input, select, search box</div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            <button
                              type="button"
                              style={{
                                border: "none",
                                borderRadius: 999,
                                padding: "10px 14px",
                                color: "#ffffff",
                                backgroundColor: brandingForm.colorPrimary,
                                fontFamily: brandingForm.fontFamily,
                                fontSize: brandingForm.appFontSize,
                                fontWeight: 700,
                              }}
                            >
                              Primary
                            </button>
                            <button
                              type="button"
                              style={{
                                borderRadius: 999,
                                padding: "10px 14px",
                                color: brandingForm.colorPrimary,
                                backgroundColor: "transparent",
                                border: `1px solid ${brandingForm.colorPrimary}`,
                                fontFamily: brandingForm.fontFamily,
                                fontSize: brandingForm.appFontSize,
                                fontWeight: 700,
                              }}
                            >
                              Outline
                            </button>
                          </div>
                          <div
                            style={{
                              borderRadius: 12,
                              padding: "12px",
                              backgroundColor: mode.foregroundColor,
                              border: `1px solid ${brandingForm.colorAccent}66`,
                              fontFamily: brandingForm.tableFontFamily || brandingForm.fontFamily,
                              fontSize: brandingForm.tableFontSize,
                              color: mode.textColor,
                            }}
                          >
                            <div style={{ marginBottom: 8, fontSize: brandingForm.tableHeaderFontSize, fontWeight: 800, color: brandingForm.colorAccent }}>
                              Anteprima tabella
                            </div>
                            <div
                              style={{
                                overflow: "hidden",
                                borderRadius: 10,
                                border: `1px solid ${brandingForm.colorSecondary}55`,
                              }}
                            >
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  fontFamily: brandingForm.tableFontFamily || brandingForm.fontFamily,
                                  fontSize: brandingForm.tableFontSize,
                                }}
                              >
                                <thead>
                                  <tr style={{ backgroundColor: brandingForm.colorPrimary, color: "#ffffff" }}>
                                    <th style={{ padding: "10px 12px", textAlign: "left", fontSize: brandingForm.tableHeaderFontSize, fontWeight: 800 }}>
                                      Dipendente
                                    </th>
                                    <th style={{ padding: "10px 12px", textAlign: "left", fontSize: brandingForm.tableHeaderFontSize, fontWeight: 800 }}>
                                      Mansione
                                    </th>
                                    <th style={{ padding: "10px 12px", textAlign: "left", fontSize: brandingForm.tableHeaderFontSize, fontWeight: 800 }}>
                                      Stato
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[
                                    ["Giulia Esposito", "Sala", "Confermato"],
                                    ["Marco Russo", "Cucina", "In review"],
                                  ].map((row, index) => (
                                    <tr
                                      key={`${mode.key}-${row[0]}`}
                                      style={{
                                        backgroundColor: index % 2 === 0 ? mode.fieldColor : mode.foregroundColor,
                                      }}
                                    >
                                      {row.map((cell) => (
                                        <td
                                          key={cell}
                                          style={{
                                            padding: "10px 12px",
                                            borderTop: `1px solid ${brandingForm.colorSecondary}33`,
                                            color: mode.tableCellTextColor,
                                            fontWeight: 700,
                                          }}
                                        >
                                          {cell}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.78 }}>
                              Font tabella: {brandingForm.tableFontFamily || brandingForm.fontFamily}
                            </div>
                          </div>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={pageTypography.panelCaption}>Tema: {brandingForm.theme}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={pageTypography.panelCaption}>Sidebar: {brandingForm.sidebarEnabled ? "attiva" : "disattiva"}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={pageTypography.panelCaption}>Layout: {brandingForm.layout}</Typography>
                  </Grid>
                </Grid>
              </Stack>
            </PagePanel>
          </Stack>
        </PagePanel>
      )}

      <AdminEntityModal
        open={companyModal.open}
        title={companyModal.value?.id ? "Modifica azienda" : "Nuova azienda"}
        value={companyModal.value}
        fields={[
          { name: "nome", label: "Nome azienda" },
          { name: "piano", label: "Piano", options: ["FREE", "BASIC", "PREMIUM"] },
        ]}
        onClose={() => setCompanyModal({ open: false, value: defaultCompany })}
        onSubmit={saveCompany}
      />

      <UserModal
        open={userModal.open}
        value={userModal.value}
        locali={locali}
        onClose={() => setUserModal({ open: false, value: defaultUser })}
        onSubmit={saveUser}
      />

      <Dialog
        open={passwordModal.open}
        onClose={() => setPasswordModal({ open: false, userId: null, username: "", nuovaPassword: "" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cambia password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography sx={pageTypography.panelCaption}>Utente: {passwordModal.username || "-"}</Typography>
            <TextField
              fullWidth
              required
              type="password"
              label="Nuova password"
              value={passwordModal.nuovaPassword}
              onChange={(event) =>
                setPasswordModal((prev) => ({
                  ...prev,
                  nuovaPassword: event.target.value,
                }))
              }
              helperText="Minimo 8 caratteri"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordModal({ open: false, userId: null, username: "", nuovaPassword: "" })}>Annulla</Button>
          <Button
            variant="contained"
            onClick={submitPasswordReset}
            disabled={passwordModal.nuovaPassword.trim().length < 8}
          >
            Aggiorna
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
